import React, { useMemo, useRef, useState } from 'react';

import { ICharacteristics } from '@r1-frontend/api-domru/full-buy/v1/equipment/dto/EquipmentProp.dto';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { EMediaWidth } from '@r1-frontend/ui-react/tokens/mediaWidth';
import { truncate } from '@r1-frontend/shared/helpers/truncate';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { useResizeObserver } from '@r1-frontend/shared/hooks/useResizeObserver';

import * as ST from './styled';

export interface IDetail {
    city?: ICurrentCity,
    description?: string,
    characteristics?: ICharacteristics[],
    marginBottom?: number,
}

const deliveryIcon = '/static/images/work-in-home/delivery.svg';
const settingIcon = '/static/images/work-in-home/setting.svg';

const Detail = ({ city, description, characteristics, marginBottom }: IDetail) => {
    const [descShowAllLines, setDescShowAllLines] = useState<boolean>(false);
    const [charShowAllLines, setCharShowAllLines] = useState<boolean>(false);
    const isMobile = useMatchMedia(EMediaWidth.mobile);

    const [hoverIndex, setHoverIndex] = useState<number>(-1);

    const characteristicsToShow: ICharacteristics[] | undefined = useMemo(() => {
        if (!characteristics) {
            return undefined;
        }
        const charArrToShow = characteristics.filter(line => line.asIs);
        return charArrToShow.length > 0 ? charArrToShow : undefined;
    }, [characteristics]);

    const ref = useRef<HTMLSpanElement>(null);

    const { width } = useResizeObserver(ref);

    const isOverflowing = useMemo((): boolean => {
        const el: HTMLSpanElement | null = ref.current;
        if (el) {
            const curOverf = el.style.overflow;
            if (!curOverf || curOverf === 'visible') {
                el.style.overflow = 'hidden';
            }
            const isOverflowing = el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
            el.style.overflow = curOverf;
            const divHeight = el.offsetHeight;
            const lineHeight = isMobile ? 20 : 24;
            const lines = divHeight / lineHeight;
            return isOverflowing || lines > 3;
        }
        return true;
    }, [width, isMobile]);

    const hasFreeDeliveryAndInstallation = useMemo(() => {
        return !!city?.hasFreeDeliveryAndInstallation;
    }, [city]);

    const maxChars = isMobile ? 9 : 20;

    return (
        <ST.DetailContainer marginBottom={marginBottom}>
            {
                description && <ST.Container data-test='detail-description-container'>
                    <ST.DetailLabel>{'Описание'}</ST.DetailLabel>
                    <ST.DetailInfo
                        ref={ref}
                        data-test='detail-description-info'
                        dangerouslySetInnerHTML={{
                            __html: description,
                        }}
                        showAllLines={descShowAllLines}
                        linesNumber={3}
                    />
                    {isOverflowing && <ST.ShowLessButton data-test='detail-description-button' onClick={() => setDescShowAllLines(prev => !prev)}>
                        {descShowAllLines ? 'Свернуть' : 'Показать больше'}
                    </ST.ShowLessButton>}
                </ST.Container>
            }
            {
                characteristicsToShow && <ST.CharContainer data-test='detail-characteristics-container'>
                    <ST.DetailLabel>{'Основные характеристики'}</ST.DetailLabel>
                    {
                        characteristicsToShow.map((character, index) => {
                            if (!charShowAllLines && index > 2) {
                                return null;
                            }
                            return (<ST.RowContainer key={character.name} data-test='detail-characteristics-list'>
                                <ST.DetailInfo data-test='detail-characteristics-title'>{character.title}</ST.DetailInfo>
                                <ST.DotsComponent />
                                <ST.DetailInfo
                                    data-test='detail-characteristics-value'
                                    onMouseEnter={() => setHoverIndex(index)}
                                    maxWidth={true}
                                >
                                    {truncate(character.value, maxChars)}
                                </ST.DetailInfo>
                                {hoverIndex === index && <ST.DetailInfoHover
                                    onMouseLeave={() => setHoverIndex(-1)}>
                                    {character.value}
                                </ST.DetailInfoHover>}
                            </ST.RowContainer>);
                        })
                    }
                    {characteristicsToShow.length > 3 &&
                        <ST.ShowLessButton data-test='detail-characteristics-button' onClick={() => setCharShowAllLines(prev => !prev)}>
                            {charShowAllLines ? 'Свернуть' : 'Показать больше'}
                        </ST.ShowLessButton>}
                </ST.CharContainer>
            }
            {hasFreeDeliveryAndInstallation && <>
                <ST.RowIconsContainer>
                    <ST.Icon imgUrl={deliveryIcon} />
                    <ST.IconInfoText data-test='detail-delivery-label'>{'Бесплатная доставка'}</ST.IconInfoText>
                </ST.RowIconsContainer>
                <ST.RowIconsContainer>
                    <ST.Icon imgUrl={settingIcon} />
                    <ST.IconInfoText data-test='detail-setting-label'>{'Бесплатная настройка'}</ST.IconInfoText>
                </ST.RowIconsContainer>
            </>
            }
        </ST.DetailContainer>
    );
};

export default Detail;
